import { useQuery } from '@tanstack/react-query';
import { apiHelpers, parseApiResponse } from '@/api/core';
import type { PaginationInfo } from '@packages/types/common/dto';

export interface ActivityLogRow {
  id: number;
  createdAt: string;
  userId: number | null;
  userEmail: string | null;
  userName: string | null;
  logType: number;
  logTypeLabel: string;
  feature: number;
  featureLabel: string;
  action: number;
  actionLabel: string;
  isError: boolean;
  message: string;
  errorCode: string | null;
  stackTrace: string | null;
  metadata: unknown;
  requestId: string | null;
  httpMethod: string | null;
  routePath: string | null;
}

export interface ActivityLogsQueryParams {
  page: number;
  limit: number;
  userId?: number;
  companyId?: number;
  siteId?: number;
  from?: string;
  to?: string;
  logType?: number;
  feature?: number;
  action?: number;
  isError?: boolean;
  messageContains?: string;
  routePathContains?: string;
}

function buildActivityLogsRequestParams(params: ActivityLogsQueryParams): Record<
  string,
  string | number | boolean
> {
  const q: Record<string, string | number | boolean> = {
    page: params.page,
    limit: Math.min(params.limit, 200),
  };
  if (params.userId !== undefined) q.userId = params.userId;
  if (params.companyId !== undefined) q.companyId = params.companyId;
  if (params.siteId !== undefined) q.siteId = params.siteId;
  if (params.from !== undefined) q.from = params.from;
  if (params.to !== undefined) q.to = params.to;
  if (params.logType !== undefined) q.logType = params.logType;
  if (params.feature !== undefined) q.feature = params.feature;
  if (params.action !== undefined) q.action = params.action;
  if (params.isError !== undefined) q.isError = params.isError;
  if (params.messageContains?.trim())
    q.messageContains = params.messageContains.trim();
  if (params.routePathContains?.trim())
    q.routePathContains = params.routePathContains.trim();
  return q;
}

export interface ActivityLogsResult {
  rows: ActivityLogRow[];
  pagination: PaginationInfo | null;
}

const activityLogsKeys = {
  all: ['activityLogs'] as const,
  list: (params: ActivityLogsQueryParams) => [...activityLogsKeys.all, params] as const,
};

export interface ActivityLogUser {
  id: number;
  name: string;
  email: string;
}

export function useActivityLogUsersByCompany(companyId: number | undefined) {
  return useQuery({
    queryKey: ['activityLogUsers', 'company', companyId],
    queryFn: async (): Promise<ActivityLogUser[]> => {
      const res = await apiHelpers.get<ActivityLogUser[]>(
        'activityLogs.usersByCompany',
        { companyId: companyId! },
      );
      const parsed = parseApiResponse<ActivityLogUser[]>(res);
      return parsed.data ?? [];
    },
    enabled: !!companyId,
  });
}

export function useActivityLogUsersBySite(siteId: number | undefined) {
  return useQuery({
    queryKey: ['activityLogUsers', 'site', siteId],
    queryFn: async (): Promise<ActivityLogUser[]> => {
      const res = await apiHelpers.get<ActivityLogUser[]>(
        'activityLogs.usersBySite',
        { siteId: siteId! },
      );
      const parsed = parseApiResponse<ActivityLogUser[]>(res);
      return parsed.data ?? [];
    },
    enabled: !!siteId,
  });
}

export function useActivityLogs(params: ActivityLogsQueryParams) {
  return useQuery({
    queryKey: activityLogsKeys.list(params),
    queryFn: async (): Promise<ActivityLogsResult> => {
      const res = await apiHelpers.get<ActivityLogRow[]>(
        'activityLogs.list',
        undefined,
        buildActivityLogsRequestParams(params)
      );
      const parsed = parseApiResponse<ActivityLogRow[]>(res);
      if (parsed.error) {
        throw new Error(parsed.error.message || 'Unknown error');
      }
      const metaPagination = parsed.meta &&
        typeof parsed.meta === 'object' &&
        'pagination' in parsed.meta
        ? ((parsed.meta as { pagination?: PaginationInfo }).pagination ?? null)
        : null;

      return {
        rows: parsed.data ?? [],
        pagination: metaPagination,
      };
    },
  });
}
